import {
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { TranscriptionLanguage } from '@treams/treams-network';
import { useToasts } from '@treams/treams-ui';

import {
	LOCK_PREFIX,
	type StartResult,
	type StreamingState,
	type StreamingTarget,
	useStreamingTranscription,
} from './useStreamingTranscription';

const LOCK_POLL_INTERVAL_MS = 2_000;

type StreamingTranscriptionContextValue = {
	state: StreamingState;
	start: (
		target: StreamingTarget,
		options: { language: TranscriptionLanguage; transcriptionID?: string | null },
	) => Promise<void>;
	resume: () => Promise<void>;
	pause: () => Promise<void>;
	finish: () => Promise<void>;
	/** Leaves a finish that keeps failing; unsaved text is kept. */
	stopFinishing: () => void;
	/** Stops before anything was said, without confirmation. */
	cancel: () => Promise<void>;
	/** Throws away this session's text after the user confirmed. */
	discard: () => Promise<void>;
	close: (options?: { discardQueue?: boolean }) => void;
	setLanguage: (language: TranscriptionLanguage) => void;
	bindTarget: (target: StreamingTarget) => void;
	isStreamingTarget: (targetID: string) => boolean;
};

const StreamingTranscriptionContext = createContext<StreamingTranscriptionContextValue | null>(
	null,
);

export function useStreamingTranscriptionContext() {
	const context = useContext(StreamingTranscriptionContext);

	if (!context) {
		throw new Error(
			'useStreamingTranscriptionContext must be used within StreamingTranscriptionProvider',
		);
	}

	return context;
}

/**
 * True while another tab holds the recording lock for this target.
 * @param targetID The conversation or note this recording belongs to.
 * @param enabled When false, the lock is not checked and the result stays false.
 */
export function useRecordingInAnotherTab(targetID: string, enabled: boolean): boolean {
	const { isStreamingTarget } = useStreamingTranscriptionContext();
	const [heldElsewhere, setHeldElsewhere] = useState(false);
	const recordingHere = isStreamingTarget(targetID);

	useEffect(() => {
		if (!enabled || recordingHere || !navigator.locks) {
			setHeldElsewhere(false);
			return;
		}

		let cancelled = false;

		const check = () => {
			navigator.locks
				.query()
				.then(({ held }) => {
					if (cancelled) return;
					setHeldElsewhere(!!held?.some((lock) => lock.name === `${LOCK_PREFIX}${targetID}`));
				})
				.catch(() => {});
		};

		check();
		const interval = setInterval(check, LOCK_POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [enabled, recordingHere, targetID]);

	return heldElsewhere;
}

export function StreamingTranscriptionProvider({ children }: PropsWithChildren) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { state, controller } = useStreamingTranscription();

	const reportStartResult = useCallback(
		(result: StartResult) => {
			if (result === 'microphone') {
				addToast(t('transcription.failedToAccessMicrophone'), {
					appearance: 'error',
					autoDismiss: true,
				});
			} else if (result === 'otherTab') {
				addToast(t('transcription.streaming.recordingInAnotherTab'), {
					appearance: 'warning',
					autoDismiss: true,
				});
			} else if (result === 'audio') {
				addToast(t('voiceMemo.error.message'), { appearance: 'error', autoDismiss: true });
			}
		},
		[addToast, t],
	);

	const sessionTargetID = state.session?.targetID;

	const actions = useMemo(
		() => ({
			start: async (
				target: StreamingTarget,
				options: { language: TranscriptionLanguage; transcriptionID?: string | null },
			) => reportStartResult(await controller.start(target, options)),
			resume: async () => reportStartResult(await controller.resume()),
			pause: () => controller.pause(),
			finish: () => controller.finish(),
			stopFinishing: () => controller.stopFinishing(),
			cancel: () => controller.cancel(),
			discard: () => controller.discard(),
			close: (options?: { discardQueue?: boolean }) => controller.close(options),
			setLanguage: (language: TranscriptionLanguage) => controller.setLanguage(language),
			bindTarget: (target: StreamingTarget) => controller.bindTarget(target),
		}),
		[controller, reportStartResult],
	);

	const isStreamingTarget = useCallback(
		(targetID: string) => sessionTargetID === targetID,
		[sessionTargetID],
	);

	const value = useMemo<StreamingTranscriptionContextValue>(
		() => ({ ...actions, state, isStreamingTarget }),
		[actions, state, isStreamingTarget],
	);

	return (
		<StreamingTranscriptionContext.Provider value={value}>
			{children}
		</StreamingTranscriptionContext.Provider>
	);
}
